import { Button } from "reactstrap";
import { useState, useRef, useEffect } from "react";
import 'react-toastify/dist/ReactToastify.css';
import { motion } from "framer-motion";
import RotatePhonePopup from "../RotatePhonePopup";
import axios from "axios";
import CompletePopup from "./completepopup";
import { useSnackbar } from "../context/SnackbarContext";
import useBadWordDetection from '../../hooks/Usebadworddetection';
import { useLanguage } from "../context/Languagecontext";
import { convertToNativeNumerals } from "../../utils/numberUtils";
import BadWordWarning from "./Badwordwarning";
import DiceButton from "./DiceButton";

const Category9Update = ({ hint, uploadedImage, originalData, username, category }) => {
    const { lanText, t } = useLanguage();
    const { showSnackbar } = useSnackbar();

    const [isUploading, setIsUploading] = useState(false);
    const [isCheckingConnection, setIsCheckingConnection] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const [cardAnimationComplete, setCardAnimationComplete] = useState(false);

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [rumourEmoji, setRumourEmoji] = useState("");

    const [userContent, setUserContent] = useState('');
    const { hasBadWord } = useBadWordDetection(userContent);
    const [isShaking, setIsShaking] = useState(false);
    const [keyboardOpen, setKeyboardOpen] = useState(false);
    const textareaRef = useRef(null);
    const topRef = useRef(null);
    const scrollTimeoutRef = useRef(null);
    const maxLength = 150;
    const typedChars = [...userContent].length;
    const remainingChars = maxLength - [...userContent].length;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const isAndroid = /Android/i.test(ua);
    const isSmallScreen = window.innerWidth < 380 && window.innerWidth !== 360;

    const rumourImages = [
        "https://lol-image-bucket.s3.ap-south-1.amazonaws.com/images/question8/1.png",
        "https://lol-image-bucket.s3.ap-south-1.amazonaws.com/images/question8/4.png",
        "https://lol-image-bucket.s3.ap-south-1.amazonaws.com/images/question8/5.png",
        "https://lol-image-bucket.s3.ap-south-1.amazonaws.com/images/question8/7.png",
        "https://lol-image-bucket.s3.ap-south-1.amazonaws.com/images/question8/8.png",
        "https://lol-image-bucket.s3.ap-south-1.amazonaws.com/images/question8/9.png",
        "https://lol-image-bucket.s3.ap-south-1.amazonaws.com/images/question8/10.png",
    ];

    const getRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

    useEffect(() => {
        setRumourEmoji(getRandom(rumourImages));
    }, []);

    useEffect(() => {
        setShouldAnimate(true);
        const timer = setTimeout(() => {
            setCardAnimationComplete(true);
        }, 700);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const handlePopState = () => {
            if (showSuccessModal) {
                setShowSuccessModal(false);
                const url = new URL(window.location.href);
                if (url.searchParams.has("success")) {
                    url.searchParams.delete("success");
                    window.history.replaceState({}, "", url.pathname);
                }
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [showSuccessModal]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("success") === "1") {
            setShowSuccessModal(true);
        }
    }, []);

    useEffect(() => {
        const isDisabled = isUploading || isCheckingConnection || keyboardOpen;
        window.dispatchEvent(new CustomEvent('dropdownDisabledState', {
            detail: { disabled: isDisabled }
        }));
    }, [isUploading, isCheckingConnection, keyboardOpen]);

    const checkInternetConnection = async () => {
        await new Promise(resolve => setTimeout(resolve, 200));
        if (!navigator.onLine) return false;
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            clearTimeout(timeoutId);
            return true;
        } catch {
            return false;
        }
    };

    const base64ToFile = (base64String, fileName) => {
        const arr = base64String.split(",");
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) u8arr[n] = bstr.charCodeAt(n);
        return new File([u8arr], fileName, { type: mime });
    };

    const triggerShake = () => {
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 600);
    };

    const handleContentChange = (e) => {
        const newValue = e.target.value;
        if ([...newValue].length > maxLength) {
            triggerShake();
            return;
        }
        setUserContent(e.target.value);
    };

    const handleFocus = (e) => {
        setKeyboardOpen(true);
        if (e && e.preventDefault) e.preventDefault();
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
        scrollTimeoutRef.current = setTimeout(() => {
            requestAnimationFrame(() => {
                if (topRef.current) {
                    const element = topRef.current;
                    const rect = element.getBoundingClientRect();
                    const scrollTop = window.scrollY || document.documentElement.scrollTop;
                    window.scrollTo({ top: rect.top + scrollTop, behavior: "smooth" });
                }
            });
        }, 100);
    };

    const handleBlur = () => {
        setKeyboardOpen(false);
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    };

    const upload = async () => {
        const deviceIp = localStorage.getItem("deviceIp");
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('id', originalData?.id || '');
            formData.append("category", category);
            formData.append("webCategory", category);
            formData.append('hint', hint || '');
            if (uploadedImage) {
                if (uploadedImage.startsWith('data:image')) {
                    formData.append('uploadedImage', base64ToFile(uploadedImage, 'hintImage.jpg'));
                } else {
                    formData.append('uploadedImage', uploadedImage);
                }
            }
            formData.append('userLocation', "allow");
            formData.append('deviceIp', deviceIp || '');
            formData.append('rumourContent', userContent.trim() || '');
            formData.append('rumourEmoji', rumourEmoji);

            await axios.post('https://api.lolcards.link/api/newinbox/create', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const url = new URL(window.location.href);
            url.searchParams.set('success', '1');
            window.history.pushState({ success: true }, '', url.toString());
            setShowSuccessModal(true);
            setUserContent('');
        } catch (error) {
            console.error('Error uploading data:', error);
            showSnackbar(t?.common?.apiFail || 'Request failed. Please try again after some time!', "error");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSendClick = async () => {
        if (!isUploading) {
            try {
                setIsCheckingConnection(true);
                const isOnline = await checkInternetConnection();
                setIsCheckingConnection(false);
                if (!isOnline) {
                    showSnackbar(t?.common?.nointertoast || "Please turn on internet connection!", "error");
                    return;
                }
                await upload();
                await axios.patch(
                    'https://api.lolcards.link/api/admin/monitoring',
                    { categoryname: category, id: originalData?.id }
                );
            } catch (error) {
                console.error('Error updating monitoring:', error);
            }
        }
    };

    return (
        <>
            <div
                className="GTRegular"
                style={{ background: "linear-gradient(0deg, #03C2FE 0%, #DE67FD 100%)", height: "100dvh" }}
            >
                <div className="h-100 pt-42 hint-bg9">
                    <div className="text-center mx-auto h-100" style={{ width: "550px", maxWidth: "90vw" }}>

                        <motion.div
                            key="text"
                            initial={{ x: 0, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            className="text-center d-flex flex-column justify-content-start h-100"
                        >
                            {/* Title */}
                            <div
                                className="text-center py-3 d-flex align-items-center"
                                style={{
                                    maxWidth: "90vw",
                                    height: "15dvh",
                                    opacity: cardAnimationComplete ? "1" : "0",
                                    transition: "opacity 0.2s ease",
                                }}
                            >
                                <p
                                    className="lh-sm mx-auto px-3 m-0 text-white"
                                    style={{ fontSize: "18px" }}
                                >
                                    {t?.rumour?.textTitle || "Write a rumour anonymously"}
                                </p>
                            </div>

                            {/* Card */}
                            <div
                                ref={topRef}
                                className="d-flex flex-column align-items-center justify-content-center mx-auto"
                                style={{
                                    marginBottom: keyboardOpen && isAndroid ? "0px" : "0px",
                                    paddingTop: isSmallScreen ? "20px" : "20px",
                                    transition: "margin 0.3s ease",
                                    width: "96%",
                                }}
                            >
                                <motion.div
                                    className={`w-100 rounded-19 position-relative mt-2 glass-card ${isShaking ? 'shake-animation' : ''}`}
                                    style={{ height: isSmallScreen ? "220px" : "240px" }}
                                    initial={{ scale: 0 }}
                                    animate={{ scale: shouldAnimate ? 1 : 0 }}
                                    transition={{ type: "spring", duration: 1, delay: 0.2 }}
                                >
                                    {/* Top white section */}
                                    <div
                                        className="diagonal-bg d-flex align-items-center justify-content-center text-black rounded-19-top Bugs"
                                        style={{
                                            height: isSmallScreen ? "95px" : "100px",
                                            background: "white",
                                            fontSize: "26px",
                                            paddingBottom: "27px",
                                        }}
                                    >
                                        {(t?.rumour?.rumourTitle || 'Rumour').charAt(0).toUpperCase() +
                                            (t?.rumour?.rumourTitle || 'Rumour').slice(1)}
                                    </div>

                                    {/* Textarea section */}
                                    <div
                                        className="px-3 pb-2 d-flex align-items-center justify-content-center GTBold text-left position-relative"
                                        style={{
                                            height: isSmallScreen ? "110px" : "120px",
                                            fontSize: "19px",
                                            wordBreak: "break-word",
                                            overflowWrap: "break-word",
                                            whiteSpace: "normal",
                                            paddingTop: "23px",
                                        }}
                                    >
                                        <textarea
                                            ref={textareaRef}
                                            value={userContent}
                                            onChange={handleContentChange}
                                            onFocus={handleFocus}
                                            onBlur={handleBlur}
                                            placeholder={t?.rumour?.placeholder || "Write a rumour..."}
                                            disabled={isUploading || isCheckingConnection}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    textareaRef.current?.blur();
                                                }
                                            }}
                                            enterKeyHint="done"
                                            className="w-100 h-100 border-0 custom-placeholder-5 GTRegular text-white bg-transparent"
                                            style={{
                                                resize: "none",
                                                outline: "none",
                                                fontSize: "17px",
                                                fontWeight: "500",
                                                height: isSmallScreen ? "95px" : "110px",
                                                lineHeight: "23px",
                                                textAlign: userContent ? "start" : "center",
                                                padding:
                                                    userContent.length > 0
                                                        ? "0px"
                                                        : isSmallScreen
                                                            ? "25px 10px 10px"
                                                            : "35px 10px 10px",
                                            }}
                                        />

                                        {/* Emoji circle */}
                                        <div
                                            style={{
                                                width: isSmallScreen ? "57px" : "60px",
                                                height: isSmallScreen ? "57px" : "60px",
                                                borderRadius: "50%",
                                                background: "white",
                                                position: "absolute",
                                                top: "-43px",
                                                left: "50%",
                                                transform: "translateX(-50%)",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    border: "1.5px solid black",
                                                    width: "100%",
                                                    height: "100%",
                                                    borderRadius: "50%",
                                                    background: "white",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <img
                                                    src={rumourEmoji}
                                                    alt="avatar"
                                                    style={{ width: "80%", height: "80%", objectFit: "contain" }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <DiceButton
                                        category={"Qmx1ZmY="}
                                        onContentFetched={(text) => setUserContent(text)}
                                        disabled={isUploading || isCheckingConnection}
                                    />
                                </motion.div>

                                {/* Char counter */}
                                <p
                                    className={`text-end pe-3 w-100 text-white my-1 GTMedium ${remainingChars <= 0 ? 'limit-reached' : ''}`}
                                    style={{
                                        opacity: cardAnimationComplete ? "1" : "0",
                                        transition: "opacity 0.2s ease",
                                    }}
                                >
                                    {convertToNativeNumerals(typedChars, lanText)}/{convertToNativeNumerals(maxLength, lanText)}
                                </p>

                                <BadWordWarning
                                    visible={hasBadWord}
                                    message={t?.common?.badWordWarning || "Avoid using offensive language"}
                                />
                            </div>

                            {/* Send Button */}
                            <div
                                className="w-100"
                                style={{
                                    marginTop: isSmallScreen ? "25px" : "25px",
                                    opacity: cardAnimationComplete ? "1" : "0",
                                    transition: "opacity 0.2s ease",
                                }}
                            >
                                <Button
                                    className="rounded-pill border-0 mx-auto mb-2 text-white"
                                    disabled={!userContent.trim()}
                                    style={{
                                        padding: "12px 1px",
                                        height: "52px",
                                        width: "75%",
                                        pointerEvents: (!isUploading && isCheckingConnection && !userContent.trim()) ? 'none' : 'auto',
                                        cursor: (!isUploading && isCheckingConnection && !userContent.trim()) ? 'not-allowed' : 'pointer',
                                        background: "black",
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    }}
                                    onClick={handleSendClick}
                                >
                                    {isUploading || isCheckingConnection ? (
                                        <div className="custom-spinner" style={{ borderTopColor: "#fff" }} />
                                    ) : (
                                        <span
                                            className="GTMedium"
                                            style={{ fontSize: "clamp(21px, 4.5vw, 27px)", whiteSpace: "nowrap" }}
                                        >
                                            {t?.common?.send || "Send"}
                                        </span>
                                    )}
                                </Button>
                            </div>
                        </motion.div>

                    </div>
                </div>

                <CompletePopup
                    showSuccessModal={showSuccessModal}
                    toggle={() => setShowSuccessModal(false)}
                    data={originalData}
                    username={username}
                    category={category}
                    t={t}
                />

                <RotatePhonePopup excludeWhen={[keyboardOpen]} />

                <style jsx>{`
                    @keyframes pulse {
                        0% { transform: scale(1.1); }
                        70% { transform: scale(1.15); }
                        100% { transform: scale(1.1); }
                    }
                    .spinner-border {
                        animation: spinner-border 0.75s linear infinite;
                    }
                    @keyframes spinner-border {
                        to { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </>
    );
};

export default Category9Update;